import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, CheckCircle2, XCircle, Trash2, Wallet, Lock, ClipboardList, Send, ShieldCheck, RotateCcw,
  CalendarClock, AlertTriangle,
} from "lucide-react";
import { api, idr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import LoadingState from "@/components/ui/loading-state";
import InputRupiah from "@/components/InputRupiah";
import { DatePicker } from "@/components/ui/date-picker";
import { formatTanggal, hariIniISO, dateDariIso, isoDariDate } from "@/lib/tanggal";
import {
  SearchInput, StatusBadge, DEAL_STATUS_META, DP_STATUS_META, PELUNASAN_STATUS_META,
  DELIVERABLE_STATUS_META, DELIVERABLE_JENIS, DELIVERABLE_JENIS_LABEL,
  SortTh, useSortState, sortRows, usePaginasi, Paginasi, unduhCsv, TombolEksporCsv,
  Modal, Field, inputCls, selectCls, textareaCls, useAkunKasBank, useCampaignMap, namaCampaign,
  usePinHapus, PinHapusDialog,
} from "./_shared";

// Ambang "mendekat" untuk badge jatuh tempo pembayaran DP/pelunasan (batch
// tanggal jatuh tempo & deadline posting) — 3 hari, konsisten dengan tenor
// pendek pembayaran endorsement (bukan utang/piutang bulanan yang di halaman
// Finance memakai ambang 30 hari).
const AMBANG_MENDEKAT_HARI = 3;

function tambahHariIso(iso, n) {
  const d = dateDariIso(iso);
  if (!d) return iso;
  d.setDate(d.getDate() + n);
  return isoDariDate(d);
}

/** "lewat" | "dekat" | "normal" — null kalau tanggal kosong (deal LAMA tanpa
 *  field ini, atau memang belum diisi). Perbandingan string ISO leksikografis
 *  aman karena formatnya seragam "YYYY-MM-DD" (sama seperti backend). */
function statusJatuhTempo(tanggalISO) {
  if (!tanggalISO) return null;
  const hariIni = hariIniISO();
  if (tanggalISO < hariIni) return "lewat";
  if (tanggalISO <= tambahHariIso(hariIni, AMBANG_MENDEKAT_HARI)) return "dekat";
  return "normal";
}

/** Badge kecil jatuh tempo — dipakai di baris tabel deal. Tidak render apa pun
 *  kalau `tanggal` kosong, supaya deal lama tanpa field ini tidak terlihat
 *  "bermasalah" (lihat docstring kol.py bagian TANGGAL JATUH TEMPO PEMBAYARAN). */
function BadgeJatuhTempo({ label, tanggal, testid }) {
  if (!tanggal) return null;
  const status = statusJatuhTempo(tanggal);
  const warna = status === "lewat" ? "text-rose-600" : status === "dekat" ? "text-amber-600" : "text-slate-500";
  return (
    <span className={`text-[10px] inline-flex items-center gap-1 ${warna}`} data-testid={testid}>
      {status === "lewat" && <AlertTriangle className="w-2.5 h-2.5 shrink-0" />}
      {label}: {formatTanggal(tanggal)}
    </span>
  );
}

/**
 * Panel deal KOL — dipakai DUA tempat: tab "Semua Deal" di Kol.jsx (kolId
 * kosong, kolom KOL ditampilkan) dan tab "Deals" di KolDetail.jsx (kolId
 * terkunci ke satu KOL). Satu sumber supaya aksi bayar DP/pelunasan/kelola
 * deliverable tidak bercabang dua implementasi berbeda.
 */
export default function DealsPanel({ kolId, kolOptions = [] }) {
  const { can } = useAuth();
  const bolehTambah = can("marketing_kol", "create");
  const bolehUpdate = can("marketing_kol", "update");
  const bolehHapus = can("marketing_kol", "delete");
  const bolehCampaign = can("marketing_campaign", "view");
  const { campaignMap } = useCampaignMap(bolehCampaign);
  const akunKasBank = useAkunKasBank();

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const { sortKey, sortDir, toggle } = useSortState("created_at", "desc");
  const [form, setForm] = useState(null);
  const [bayarState, setBayarState] = useState(null); // {deal, jenis: "dp"|"pelunasan"}
  const [deliverableDeal, setDeliverableDeal] = useState(null);
  const [jatuhTempoState, setJatuhTempoState] = useState(null); // {deal, jatuh_tempo_dp, jatuh_tempo_pelunasan}
  const [busy, setBusy] = useState(false);
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();

  const { data, loading, error, refetch } = useDataQuery(
    ["marketing", "kol-deals", kolId || "semua", statusFilter],
    () => api.get("/marketing/kol/deals", {
      params: { ...(kolId ? { kol_id: kolId } : {}), ...(statusFilter ? { status: statusFilter } : {}) },
    }).then((r) => r.data),
  );

  const filtered = useMemo(() => {
    const rows = data || [];
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((d) => d.kol_nama?.toLowerCase().includes(t) || d.code?.toLowerCase().includes(t) || d.deskripsi?.toLowerCase().includes(t));
  }, [data, q]);
  const sorted = sortRows(filtered, sortKey, sortDir);
  const { page, setPage, totalPages, awal, akhir } = usePaginasi(sorted.length, 10);
  const halaman = sorted.slice(awal, akhir);

  const eksporCsv = () => {
    unduhCsv(`deal-kol-${hariIniISO()}.csv`, [
      { label: "Kode", value: (r) => r.code },
      { label: "KOL", value: (r) => r.kol_nama },
      { label: "Campaign", value: (r) => namaCampaign(campaignMap, r.campaign_id) },
      { label: "Nilai Deal", value: (r) => r.nilai_deal },
      { label: "DP", value: (r) => r.dp_nominal },
      { label: "Status", value: (r) => DEAL_STATUS_META[r.status]?.label || r.status },
      { label: "Status DP", value: (r) => DP_STATUS_META[r.dp_status]?.label || r.dp_status },
      { label: "Jatuh Tempo DP", value: (r) => r.jatuh_tempo_dp || "" },
      { label: "Status Pelunasan", value: (r) => PELUNASAN_STATUS_META[r.pelunasan_status]?.label || r.pelunasan_status },
      { label: "Jatuh Tempo Pelunasan", value: (r) => r.jatuh_tempo_pelunasan || "" },
      { label: "Periode", value: (r) => `${r.tanggal_mulai} s/d ${r.tanggal_selesai}` },
    ], sorted);
  };

  const setujui = async (d) => {
    if (!window.confirm(`Setujui deal ${d.code} dengan ${d.kol_nama}?`)) return;
    try { await api.post(`/marketing/kol/deals/${d.id}/approve`); toast.success("Deal disetujui"); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui deal"); }
  };
  const tolak = async (d) => {
    const alasan = window.prompt(`Alasan menolak deal ${d.code}?`) || "";
    if (!alasan.trim()) return;
    try { await api.post(`/marketing/kol/deals/${d.id}/reject`, { alasan }); toast.success("Deal ditolak"); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak deal"); }
  };
  const hapus = async (d) => {
    if (!window.confirm(`Hapus deal ${d.code}?`)) return;
    const aksi = (pin) => api.delete(`/marketing/kol/deals/${d.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_kol", targetId: d.id, ringkasan: `Hapus deal ${d.code}` });
    if (langsung) { toast.success("Deal dihapus"); refetch(); }
  };

  const simpanDeal = async () => {
    if (!form.kol_id) { toast.error("Pilih KOL dulu"); return; }
    if (!form.nilai_deal || form.nilai_deal <= 0) { toast.error("Nilai deal wajib lebih dari 0"); return; }
    if (!form.tanggal_selesai) { toast.error("Tanggal selesai wajib diisi"); return; }
    setBusy(true);
    try {
      const r = await api.post("/marketing/kol/deals", { ...form, campaign_id: form.campaign_id || null });
      toast.success(r.data.message || "Deal diajukan");
      setForm(null);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan deal"); }
    finally { setBusy(false); }
  };

  const submitBayar = async () => {
    if (!bayarState?.sumber_akun_id) { toast.error("Pilih akun sumber pembayaran"); return; }
    setBusy(true);
    try {
      const endpoint = bayarState.jenis === "dp" ? "bayar-dp" : "bayar-pelunasan";
      const r = await api.post(`/marketing/kol/deals/${bayarState.deal.id}/${endpoint}`, {
        sumber_akun_id: bayarState.sumber_akun_id, tanggal: bayarState.tanggal || hariIniISO(),
      });
      toast.success(r.data.message || "Pembayaran tercatat");
      setBayarState(null);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mencatat pembayaran"); }
    finally { setBusy(false); }
  };

  const submitJatuhTempo = async () => {
    setBusy(true);
    try {
      // Endpoint SEMPIT khusus dua tanggal ini (lihat docstring
      // PATCH .../jatuh-tempo di kol.py) — string kosong menghapus, field yang
      // tidak dikirim dibiarkan. Modal ini selalu mengirim keduanya (idempoten
      // kalau tidak diubah), jadi aman dikirim apa adanya.
      await api.patch(`/marketing/kol/deals/${jatuhTempoState.deal.id}/jatuh-tempo`, {
        jatuh_tempo_dp: jatuhTempoState.jatuh_tempo_dp,
        jatuh_tempo_pelunasan: jatuhTempoState.jatuh_tempo_pelunasan,
      });
      toast.success("Jatuh tempo pembayaran diperbarui");
      setJatuhTempoState(null);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memperbarui jatuh tempo"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="mkt-deals-panel">
      <div className="flex items-center gap-2 flex-wrap justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput value={q} onChange={setQ} placeholder="Cari KOL/kode deal..." testid="mkt-deal-cari" className="w-56" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="mkt-deal-filter-status"
                  className={`${selectCls} h-9 w-auto`}>
            <option value="">Semua Status</option>
            {Object.entries(DEAL_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <TombolEksporCsv onClick={eksporCsv} testid="mkt-deal-export" />
          {bolehTambah && (
            <button onClick={() => setForm({ kol_id: kolId || "", campaign_id: "", deskripsi: "", nilai_deal: 0, dp_nominal: 0, tanggal_mulai: hariIniISO(), tanggal_selesai: "", catatan: "", jatuh_tempo_dp: "", jatuh_tempo_pelunasan: "" })}
                    data-testid="mkt-deal-tambah"
                    className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Ajukan Deal
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                {!kolId && <SortTh label="KOL" sortKeyName="kol_nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-deal-sort-kol" />}
                <th className="px-3 py-2.5 font-semibold">Kode / Campaign</th>
                <SortTh label="Nilai Deal" sortKeyName="nilai_deal" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="mkt-deal-sort-nilai" />
                <th className="px-3 py-2.5 font-semibold">DP / Pelunasan</th>
                <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-deal-sort-status" />
                <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState loading={loading} error={error} onRetry={refetch} kosong={!loading && !error && halaman.length === 0}
                            varian="tabel" kolom={kolId ? 5 : 6} pesanKosong="Belum ada deal." data-testid="mkt-deal-state">
                {halaman.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50/60 align-top" data-testid={`mkt-deal-row-${d.id}`}>
                    {!kolId && <td className="px-3 py-2.5 font-medium text-slate-900">{d.kol_nama}</td>}
                    <td className="px-3 py-2.5">
                      <p className="font-mono text-[11px] text-slate-500">{d.code}</p>
                      <p className="text-slate-600 text-xs">{namaCampaign(campaignMap, d.campaign_id)}</p>
                      <p className="text-slate-400 text-[11px]">{formatTanggal(d.tanggal_mulai)} – {formatTanggal(d.tanggal_selesai)}</p>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{idr(d.nilai_deal)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-col gap-1 items-start">
                        <StatusBadge meta={DP_STATUS_META} value={d.dp_status} />
                        {/* Jangan tampilkan jatuh tempo DP kalau DP sudah dibayar/tidak ada
                            — keputusan owner (batch tanggal jatuh tempo & deadline posting). */}
                        {d.dp_status !== "dibayar" && d.dp_status !== "tidak_ada" && (
                          <BadgeJatuhTempo label="Jatuh tempo DP" tanggal={d.jatuh_tempo_dp} testid={`mkt-deal-jatuh-tempo-dp-${d.id}`} />
                        )}
                        <StatusBadge meta={PELUNASAN_STATUS_META} value={d.pelunasan_status} />
                        {d.pelunasan_status !== "dibayar" && (
                          <BadgeJatuhTempo label="Jatuh tempo pelunasan" tanggal={d.jatuh_tempo_pelunasan} testid={`mkt-deal-jatuh-tempo-pelunasan-${d.id}`} />
                        )}
                        {d.pelunasan_locked && (
                          <span className="inline-flex items-start gap-1 text-[10px] text-rose-600 max-w-[220px]">
                            <Lock className="w-3 h-3 shrink-0 mt-0.5" /> {d.pelunasan_locked_reason}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5"><StatusBadge meta={DEAL_STATUS_META} value={d.status} /></td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          {d.status === "pending_approval" && bolehUpdate && (
                            <>
                              <button onClick={() => setujui(d)} data-testid={`mkt-deal-setujui-${d.id}`}
                                      className="px-2 h-7 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-medium inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Setujui
                              </button>
                              <button onClick={() => tolak(d)} data-testid={`mkt-deal-tolak-${d.id}`}
                                      className="px-2 h-7 rounded bg-rose-50 hover:bg-rose-100 text-rose-700 text-[11px] font-medium inline-flex items-center gap-1">
                                <XCircle className="w-3 h-3" /> Tolak
                              </button>
                            </>
                          )}
                          {["approved", "berjalan"].includes(d.status) && bolehUpdate && d.dp_status === "belum_dibayar" && (
                            <button onClick={() => setBayarState({ deal: d, jenis: "dp", sumber_akun_id: "", tanggal: hariIniISO() })}
                                    data-testid={`mkt-deal-bayar-dp-${d.id}`}
                                    className="px-2 h-7 rounded bg-sky-50 hover:bg-sky-100 text-sky-700 text-[11px] font-medium inline-flex items-center gap-1">
                              <Wallet className="w-3 h-3" /> Bayar DP
                            </button>
                          )}
                          {["approved", "berjalan"].includes(d.status) && bolehUpdate && d.dp_status !== "belum_dibayar" && d.pelunasan_status === "belum_dibayar" && (
                            <button onClick={() => !d.pelunasan_locked && setBayarState({ deal: d, jenis: "pelunasan", sumber_akun_id: "", tanggal: hariIniISO() })}
                                    disabled={d.pelunasan_locked} title={d.pelunasan_locked ? d.pelunasan_locked_reason : ""}
                                    data-testid={`mkt-deal-bayar-pelunasan-${d.id}`}
                                    className="px-2 h-7 rounded bg-sky-50 hover:bg-sky-100 text-sky-700 text-[11px] font-medium inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                              {d.pelunasan_locked ? <Lock className="w-3 h-3" /> : <Wallet className="w-3 h-3" />} Bayar Pelunasan
                            </button>
                          )}
                          {bolehUpdate && (
                            <button onClick={() => setDeliverableDeal(d)} data-testid={`mkt-deal-deliverable-${d.id}`}
                                    className="px-2 h-7 rounded border border-slate-200 hover:bg-slate-50 text-slate-600 text-[11px] font-medium inline-flex items-center gap-1">
                              <ClipboardList className="w-3 h-3" /> Deliverable
                            </button>
                          )}
                          {/* Tombol disembunyikan kalau DP & pelunasan sama-sama sudah lunas/
                              tidak ada — tidak ada lagi yang bisa diedit (lihat gerbang field
                              yang sama di dalam modal jatuh tempo). */}
                          {bolehUpdate && !((d.dp_status === "dibayar" || d.dp_status === "tidak_ada") && d.pelunasan_status === "dibayar") && (
                            <button onClick={() => setJatuhTempoState({ deal: d, jatuh_tempo_dp: d.jatuh_tempo_dp || "", jatuh_tempo_pelunasan: d.jatuh_tempo_pelunasan || "" })}
                                    data-testid={`mkt-deal-jatuh-tempo-${d.id}`}
                                    className="px-2 h-7 rounded border border-slate-200 hover:bg-slate-50 text-slate-600 text-[11px] font-medium inline-flex items-center gap-1">
                              <CalendarClock className="w-3 h-3" /> Jatuh Tempo
                            </button>
                          )}
                          {["pending_approval", "rejected"].includes(d.status) && bolehHapus && (
                            <button onClick={() => hapus(d)} data-testid={`mkt-deal-hapus-${d.id}`}
                                    className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 grid place-items-center">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </LoadingState>
            </tbody>
          </table>
        </div>
        <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-deal-paginasi" />
      </div>

      {/* Form ajukan deal */}
      <Modal open={!!form} onClose={() => setForm(null)} title="Ajukan Deal KOL" testid="mkt-deal-modal"
             footer={<>
               <button onClick={() => setForm(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={simpanDeal} disabled={busy} data-testid="mkt-deal-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                 {busy ? "Menyimpan..." : "Ajukan"}
               </button>
             </>}>
        {form && (
          <>
            {!kolId && (
              <Field label="KOL" required>
                <select value={form.kol_id} onChange={(e) => setForm((f) => ({ ...f, kol_id: e.target.value }))}
                        data-testid="mkt-deal-f-kol" className={selectCls}>
                  <option value="">— pilih KOL —</option>
                  {kolOptions.map((k) => <option key={k.id} value={k.id}>{k.nama} ({k.platform})</option>)}
                </select>
              </Field>
            )}
            {bolehCampaign && (
              <Field label="Campaign (opsional)">
                <select value={form.campaign_id} onChange={(e) => setForm((f) => ({ ...f, campaign_id: e.target.value }))}
                        data-testid="mkt-deal-f-campaign" className={selectCls}>
                  <option value="">— tidak terkait campaign —</option>
                  {Object.values(campaignMap).map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
                </select>
              </Field>
            )}
            <Field label="Deskripsi">
              <input value={form.deskripsi} onChange={(e) => setForm((f) => ({ ...f, deskripsi: e.target.value }))}
                     data-testid="mkt-deal-f-deskripsi" className={inputCls} placeholder="cth. 3x Post Feed + 5x Story" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nilai Deal (Rp)" required>
                <InputRupiah value={form.nilai_deal} onChange={(v) => setForm((f) => ({ ...f, nilai_deal: v }))}
                             testid="mkt-deal-f-nilai" className={inputCls} />
              </Field>
              <Field label="DP (Rp)" hint="Kosongkan/0 kalau tanpa DP">
                <InputRupiah value={form.dp_nominal} onChange={(v) => setForm((f) => ({ ...f, dp_nominal: v }))}
                             testid="mkt-deal-f-dp" className={inputCls} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tanggal Mulai" required>
                <DatePicker value={form.tanggal_mulai} onChange={(v) => setForm((f) => ({ ...f, tanggal_mulai: v }))} data-testid="mkt-deal-f-mulai" />
              </Field>
              <Field label="Tanggal Selesai" required>
                <DatePicker value={form.tanggal_selesai} onChange={(v) => setForm((f) => ({ ...f, tanggal_selesai: v }))}
                            min={form.tanggal_mulai} data-testid="mkt-deal-f-selesai" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Jatuh Tempo DP (opsional)" hint="Beda dari tanggal selesai kontrak">
                <DatePicker value={form.jatuh_tempo_dp} onChange={(v) => setForm((f) => ({ ...f, jatuh_tempo_dp: v }))} data-testid="mkt-deal-f-jatuh-tempo-dp" />
              </Field>
              <Field label="Jatuh Tempo Pelunasan (opsional)">
                <DatePicker value={form.jatuh_tempo_pelunasan} onChange={(v) => setForm((f) => ({ ...f, jatuh_tempo_pelunasan: v }))} data-testid="mkt-deal-f-jatuh-tempo-pelunasan" />
              </Field>
            </div>
            <Field label="Catatan">
              <textarea value={form.catatan} rows={2} onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))}
                        data-testid="mkt-deal-f-catatan" className={textareaCls} />
            </Field>
          </>
        )}
      </Modal>

      {/* Modal bayar DP/pelunasan */}
      <Modal open={!!bayarState} onClose={() => setBayarState(null)}
             title={bayarState?.jenis === "dp" ? "Bayar DP Deal" : "Bayar Pelunasan Deal"} maxWidth="max-w-sm" testid="mkt-deal-bayar-modal"
             footer={<>
               <button onClick={() => setBayarState(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={submitBayar} disabled={busy} data-testid="mkt-deal-bayar-submit"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                 {busy ? "Memproses..." : "Bayar"}
               </button>
             </>}>
        {bayarState && (
          <>
            <p className="text-sm text-slate-600">
              {bayarState.deal.kol_nama} — {bayarState.deal.code}: <span className="font-mono font-semibold">
                {idr(bayarState.jenis === "dp" ? bayarState.deal.dp_nominal : (bayarState.deal.nilai_deal - bayarState.deal.dp_nominal))}
              </span>
            </p>
            <Field label="Dari Akun" required>
              <select value={bayarState.sumber_akun_id} onChange={(e) => setBayarState((s) => ({ ...s, sumber_akun_id: e.target.value }))}
                      data-testid="mkt-deal-bayar-akun" className={selectCls}>
                <option value="">Pilih akun...</option>
                {akunKasBank.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
              </select>
            </Field>
            <Field label="Tanggal">
              <DatePicker value={bayarState.tanggal} onChange={(v) => setBayarState((s) => ({ ...s, tanggal: v }))} data-testid="mkt-deal-bayar-tanggal" />
            </Field>
          </>
        )}
      </Modal>

      {/* Modal atur jatuh tempo DP/pelunasan — endpoint SEMPIT terpisah dari
          field finansial (lihat docstring PATCH .../jatuh-tempo di kol.py),
          jadi aman diedit kapan pun tanpa gerbang status/approval deal. */}
      <Modal open={!!jatuhTempoState} onClose={() => setJatuhTempoState(null)} title="Atur Jatuh Tempo Pembayaran" maxWidth="max-w-sm" testid="mkt-deal-jatuh-tempo-modal"
             footer={<>
               <button onClick={() => setJatuhTempoState(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
               <button onClick={submitJatuhTempo} disabled={busy} data-testid="mkt-deal-jatuh-tempo-simpan"
                       className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                 {busy ? "Menyimpan..." : "Simpan"}
               </button>
             </>}>
        {jatuhTempoState && (
          <>
            <p className="text-sm text-slate-600">{jatuhTempoState.deal.kol_nama} — {jatuhTempoState.deal.code}</p>
            {/* Sembunyikan field DP kalau tanpa DP/DP sudah lunas — mengedit jatuh
                tempo yang sudah tidak relevan cuma membingungkan (sama aturannya
                dengan badge di tabel). */}
            {jatuhTempoState.deal.dp_status !== "dibayar" && jatuhTempoState.deal.dp_status !== "tidak_ada" && (
              <Field label="Jatuh Tempo DP" hint="Kosongkan untuk menghapus">
                <DatePicker value={jatuhTempoState.jatuh_tempo_dp} onChange={(v) => setJatuhTempoState((s) => ({ ...s, jatuh_tempo_dp: v }))} data-testid="mkt-deal-jatuh-tempo-f-dp" />
              </Field>
            )}
            {jatuhTempoState.deal.pelunasan_status !== "dibayar" && (
              <Field label="Jatuh Tempo Pelunasan" hint="Kosongkan untuk menghapus">
                <DatePicker value={jatuhTempoState.jatuh_tempo_pelunasan} onChange={(v) => setJatuhTempoState((s) => ({ ...s, jatuh_tempo_pelunasan: v }))} data-testid="mkt-deal-jatuh-tempo-f-pelunasan" />
              </Field>
            )}
          </>
        )}
      </Modal>

      {/* Modal kelola deliverable */}
      <DeliverableModal deal={deliverableDeal} onClose={() => setDeliverableDeal(null)} bolehUpdate={bolehUpdate} bolehHapus={bolehHapus}
                        onBerubah={refetch} />

      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(refetch)} />
    </div>
  );
}

function DeliverableModal({ deal, onClose, bolehUpdate, bolehHapus, onBerubah }) {
  const [busy, setBusy] = useState(false);
  const [tambah, setTambah] = useState({ jenis: DELIVERABLE_JENIS[0], deskripsi: "", due_date: hariIniISO() });
  const [submitFor, setSubmitFor] = useState(null); // {id, submitted_url, catatan}
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();

  const { data, loading, refetch } = useDataQuery(
    ["marketing", "kol-deliverables", deal?.id],
    () => api.get("/marketing/kol/deliverables", { params: { deal_id: deal.id } }).then((r) => r.data),
    { enabled: !!deal },
  );

  const tambahDeliverable = async () => {
    if (!tambah.due_date) { toast.error("Tenggat wajib diisi"); return; }
    setBusy(true);
    try {
      await api.post("/marketing/kol/deliverables", { ...tambah, deal_id: deal.id });
      toast.success("Deliverable ditambahkan");
      setTambah({ jenis: DELIVERABLE_JENIS[0], deskripsi: "", due_date: hariIniISO() });
      refetch(); onBerubah?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah deliverable"); }
    finally { setBusy(false); }
  };

  const submitDeliv = async () => {
    setBusy(true);
    try {
      await api.patch(`/marketing/kol/deliverables/${submitFor.id}/submit`, { submitted_url: submitFor.submitted_url, catatan: submitFor.catatan });
      toast.success("Deliverable ditandai submitted");
      setSubmitFor(null); refetch(); onBerubah?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal submit deliverable"); }
    finally { setBusy(false); }
  };

  const verifikasi = async (d, ok) => {
    try {
      await api.patch(`/marketing/kol/deliverables/${d.id}/${ok ? "approve" : "reject"}`, { catatan: "" });
      toast.success(ok ? "Deliverable diverifikasi" : "Dikirim balik untuk revisi");
      refetch(); onBerubah?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses deliverable"); }
  };

  const hapusDeliv = async (d) => {
    if (!window.confirm("Hapus deliverable ini?")) return;
    const aksi = (pin) => api.delete(`/marketing/kol/deliverables/${d.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_kol", targetId: d.id, ringkasan: "Hapus deliverable KOL" });
    if (langsung) { toast.success("Deliverable dihapus"); refetch(); onBerubah?.(); }
  };

  return (
    <Modal open={!!deal} onClose={onClose} title={deal ? `Deliverable — ${deal.kol_nama} (${deal.code})` : ""} maxWidth="max-w-2xl" testid="mkt-deliverable-modal">
      {deal && (
        <>
          {bolehUpdate && (
            <div className="flex items-end gap-2 border border-slate-200 rounded-lg p-3 bg-slate-50/60">
              <div className="flex-1">
                <label className="text-[11px] font-medium text-slate-500">Jenis</label>
                <select value={tambah.jenis} onChange={(e) => setTambah((t) => ({ ...t, jenis: e.target.value }))}
                        data-testid="mkt-deliv-f-jenis" className={`${selectCls} h-9 mt-1`}>
                  {DELIVERABLE_JENIS.map((j) => <option key={j} value={j}>{DELIVERABLE_JENIS_LABEL[j]}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <label className="text-[11px] font-medium text-slate-500">Deskripsi</label>
                <input value={tambah.deskripsi} onChange={(e) => setTambah((t) => ({ ...t, deskripsi: e.target.value }))}
                       data-testid="mkt-deliv-f-deskripsi" className={`${inputCls} h-9 mt-1`} />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500">Tenggat</label>
                <DatePicker value={tambah.due_date} onChange={(v) => setTambah((t) => ({ ...t, due_date: v }))} className="mt-1 w-36" data-testid="mkt-deliv-f-due" />
              </div>
              <button onClick={tambahDeliverable} disabled={busy} data-testid="mkt-deliv-tambah"
                      className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50 shrink-0">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
            <LoadingState loading={loading} kosong={!loading && (data || []).length === 0} varian="tabel" kolom={1}
                          pesanKosong="Belum ada deliverable untuk deal ini." data-testid="mkt-deliv-state">
              {(data || []).map((dl) => (
                <li key={dl.id} className="p-3 space-y-1.5" data-testid={`mkt-deliv-row-${dl.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{DELIVERABLE_JENIS_LABEL[dl.jenis] || dl.jenis}{dl.deskripsi ? ` — ${dl.deskripsi}` : ""}</p>
                      <p className="text-xs text-slate-500">Tenggat {formatTanggal(dl.due_date)}</p>
                    </div>
                    <StatusBadge meta={DELIVERABLE_STATUS_META} value={dl.status} />
                  </div>
                  {bolehUpdate && (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {["pending", "late"].includes(dl.status) && (
                        <button onClick={() => setSubmitFor({ id: dl.id, submitted_url: "", catatan: "" })}
                                data-testid={`mkt-deliv-submit-${dl.id}`}
                                className="px-2 h-7 rounded bg-sky-50 hover:bg-sky-100 text-sky-700 text-[11px] font-medium inline-flex items-center gap-1">
                          <Send className="w-3 h-3" /> Submit
                        </button>
                      )}
                      {dl.status === "submitted" && (
                        <>
                          <button onClick={() => verifikasi(dl, true)} data-testid={`mkt-deliv-verif-${dl.id}`}
                                  className="px-2 h-7 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-medium inline-flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> Verifikasi
                          </button>
                          <button onClick={() => verifikasi(dl, false)} data-testid={`mkt-deliv-revisi-${dl.id}`}
                                  className="px-2 h-7 rounded bg-amber-50 hover:bg-amber-100 text-amber-700 text-[11px] font-medium inline-flex items-center gap-1">
                            <RotateCcw className="w-3 h-3" /> Minta Revisi
                          </button>
                        </>
                      )}
                      {bolehHapus && (
                        <button onClick={() => hapusDeliv(dl)} data-testid={`mkt-deliv-hapus-${dl.id}`}
                                className="w-7 h-7 rounded hover:bg-rose-50 text-rose-600 grid place-items-center">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                  {dl.submitted_url && <p className="text-[11px] text-slate-500">Bukti: <a href={dl.submitted_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">{dl.submitted_url}</a></p>}
                </li>
              ))}
            </LoadingState>
          </ul>

          {submitFor && (
            <div className="border border-sky-200 bg-sky-50/50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-sky-800">Submit bukti deliverable</p>
              <input value={submitFor.submitted_url} placeholder="Link bukti (URL post/story/dsb)"
                     onChange={(e) => setSubmitFor((s) => ({ ...s, submitted_url: e.target.value }))}
                     data-testid="mkt-deliv-submit-url" className={`${inputCls} h-9`} />
              <textarea value={submitFor.catatan} placeholder="Catatan (opsional)" rows={2}
                        onChange={(e) => setSubmitFor((s) => ({ ...s, catatan: e.target.value }))}
                        data-testid="mkt-deliv-submit-catatan" className={textareaCls} />
              <div className="flex justify-end gap-2">
                <button onClick={() => setSubmitFor(null)} className="h-8 px-3 rounded-lg border border-slate-200 text-xs">Batal</button>
                <button onClick={submitDeliv} disabled={busy} data-testid="mkt-deliv-submit-kirim"
                        className="h-8 px-3 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-medium disabled:opacity-50">
                  {busy ? "Mengirim..." : "Kirim"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(() => { refetch(); onBerubah?.(); })} />
    </Modal>
  );
}
